import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {ScaleLinear} from 'd3';
import * as d3 from 'd3';
import React from 'react';

import {Part} from 'BioJS';
import * as selectioncontextmenu from 'components/SelectionContextMenu';
import {Sequence} from 'components/Sequence';
import {LayeringMode, PolymerLayout, SliceMapType} from 'constants/MapsConstants';
import FileStates from 'FileStates';
import {BioentityPopoverContent} from 'molbio/BioentityPopoverContent';
import {
  getAnnotationLabelData,
  getAnnotationPathParams,
  AnnotationRenderParams,
} from 'molbio/maps/linear/AnnotationsHelpers';
import ComponentWrapper from 'molbio/maps/linear/ComponentWrapper';
import {LayersDict, timesPerSlice, whichDisplay} from 'molbio/maps/linear/LayeringHelpers';
import {
  getColor,
  PART_ANNOTATION_ARCHIVED_OPACITY,
  PART_NAME_FONT_SIZE,
  SINGLE_ANNOTATION_HEIGHT,
} from 'molbio/maps/PartsViewConstants';
import Type from 'molbio/maps/Type';
import getTestProps from 'test/lib/cypress/getTestProps';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import * as Colors from 'ui-library/Colors';
import Popover from 'ui-library/floating-ui/Popover';

function getAnnotationRenderParams(
  /** The Part being rendered */
  part: Part,
  /** The Backbone sequence being rendered */
  sequence: Sequence,
  /** A Type object; the type for the annotation label */
  type: Type,
  /** The index at which the part rendering starts */
  start: number,
  /** The index at which the part rendering ends */
  end: number,
  /** The width in pixels of the annotation */
  dX: number,
  /** The height in pixels of the annotation */
  dY: number
): AnnotationRenderParams {
  const outOfSync = !part.isInSync(sequence._sequence());
  const isArchived = part.sequence().isArchived();
  return {
    start,
    end,
    width: dX,
    height: dY,
    tipWidth: Math.min(dX, type.width(1)),
    arrowsOffset: 6,
    arrowWidth: 3,
    arrowSpacing: 3,
    color: getColor(outOfSync, isArchived),
    labelColor: isArchived ? Colors.NEAR_BLACK : Colors.WHITE,
    outsideLabelColor: Colors.NEAR_BLACK,
    forceOpacity: isArchived ? PART_ANNOTATION_ARCHIVED_OPACITY : 1.0,
    forceName: part.name() + (isArchived ? ' (archived)' : ''),
    outOfSync,
  };
}

function getTooltipText(name: string | null, isInSync: boolean): string | null {
  const outOfSyncMessage = 'This part does not match the containing sequence';
  if (isInSync) {
    return name;
  } else {
    return name == null ? outOfSyncMessage : `${name}\n${outOfSyncMessage}`;
  }
}

interface SequencePartAnnotationsViewProps {
  /** A function that takes the index of a residue and returns its x-axis position on the slice */
  xFn: ScaleLinear<number, number>;
  /** A function that takes the y-index of a part annotation and returns its y-axis position on the slice */
  yFn: ScaleLinear<number, number>;
  /** The index at which the slice starts */
  sliceStart: number;
  /** The index at which the slice ends */
  sliceEnd: number;
  /** In the minimap, the residue index at the LHS of the viewport */
  leftBP: number | null;
  /** In the minimap, the residue index at the RGS of the viewport */
  rightBP: number | null;
  /** The width in pixels of the slice */
  visibleWidth: number;
  /** The Backbone sequence being rendered */
  sequence: Sequence;
  /** A Type model; the type of the residues being annotated */
  residuesType: Type;
  /** A list of context menu option names to display when a part annotation is right-clicked */
  contextMenuOptions: Array<string>;
  /** What type of map this view is being drawn on */
  mapType: SliceMapType;
  /** Flat object layout data */
  layout: PolymerLayout;
  /** A function that triggers a redraw of the slice wrapper. */
  redraw: () => void;
  /** The Parts that appear on this sequence. */
  sliceParts: Array<Part>;
  /** The ordinal y-indexes of the features on this layer. */
  layersDict: LayersDict;
  /** How to layer features. */
  layeringMode: LayeringMode;
}

export default function SequencePartAnnotationsView({
  xFn,
  yFn,
  sliceStart,
  sliceEnd,
  leftBP,
  rightBP,
  visibleWidth,
  sequence,
  residuesType,
  contextMenuOptions,
  mapType,
  layout,
  redraw,
  sliceParts,
  layersDict,
  layeringMode,
}: SequencePartAnnotationsViewProps): JSX.Element {
  // Always redraw layer wrapper; it'll account for the number of parts
  redraw();

  const type = new Type(PART_NAME_FONT_SIZE);

  return (
    <svg>
      <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={true}>
        <>
          {sliceParts.map((part) => {
            const partSegments = timesPerSlice(
              part,
              sliceStart,
              sliceEnd,
              part.drawStart(sequence),
              part.drawEnd(sequence)
            );
            return partSegments.map((partSegment, partSegmentIndex) => {
              const partId = part.sequence().id;

              const {start, end} = whichDisplay(
                sliceStart,
                sliceEnd,
                part.drawStart(sequence),
                part.drawEnd(sequence),
                partSegmentIndex,
                sequence.length()
              )!;

              const params = getAnnotationRenderParams(
                part,
                sequence,
                type,
                start,
                end,
                xFn(end) - xFn(start),
                SINGLE_ANNOTATION_HEIGHT
              );
              const {
                fillD,
                strokeLeftD,
                strokeMiddleD,
                strokeRightD,
                strokeLeftRender,
                strokeRightRender,
                strokeMiddleRender,
              } = getAnnotationPathParams(params, sliceStart, sliceEnd, 0);
              const translateX = xFn(start);
              const translateY = yFn(layersDict[part.get(layeringMode)]);

              const {displayName, labelData, spaceInside, inside} = getAnnotationLabelData(
                params,
                type,
                residuesType,
                part.name(),
                0,
                layeringMode,
                visibleWidth,
                leftBP,
                rightBP,
                2,
                'none'
              );

              const strokeAttrs = {
                stroke: part.selected() ? '#111' : d3.rgb(params.color).darker().formatHex(),
                strokeOpacity: 0.7,
                fill: 'none',
              };

              return (
                <g
                  className={classNames(css(styles.pointer), 'layer-group')}
                  onMouseDown={(e) => {
                    part.click(sequence, e.shiftKey);
                  }}
                  onDoubleClick={(e) => {
                    part.dblClick(sequence, e.shiftKey);
                  }}
                  onContextMenu={(e) => {
                    part.click(sequence, e.shiftKey);
                    selectioncontextmenu.open(e.nativeEvent, sequence, contextMenuOptions);
                  }}
                  key={`${partId}-${partSegmentIndex}`}
                >
                  {/** If we're in the minimap, use the old Bootstrap tooltip */}
                  <TooltipWrapper
                    tooltipTitle={
                      mapType === SliceMapType.minimap && !spaceInside
                        ? getTooltipText(part.name(), part.isInSync(sequence._sequence()))
                        : null
                    }
                  >
                    <Popover
                      renderContent={() => (
                        <BioentityPopoverContent
                          id={partId}
                          onClickName={() => {
                            FileStates.get().openFolderItem(partId);
                          }}
                          validationExtras={null}
                        />
                      )}
                      isDisabled={mapType === SliceMapType.minimap}
                    >
                      <g
                        {...getTestProps({
                          component: 'SequencePartAnnotationsView',
                          element: 'root',
                        })}
                        transform={`translate(${translateX}, ${translateY})`}
                      >
                        {/* Fill */}
                        <path d={fillD} fill={params.color} fillOpacity={params.forceOpacity} />
                        {/* Left side stroke */}
                        <path d={strokeLeftD} shapeRendering={strokeLeftRender} {...strokeAttrs} />
                        {/* Right side stroke */}
                        <path d={strokeRightD} shapeRendering={strokeRightRender} {...strokeAttrs} />
                        {/* Top and bottom stroke */}
                        <path d={strokeMiddleD} shapeRendering={strokeMiddleRender} {...strokeAttrs} />
                        {labelData.map((x, index) => (
                          <text
                            x={x}
                            y={SINGLE_ANNOTATION_HEIGHT - 2}
                            fontSize={type.fontSize}
                            fill={inside ? params.labelColor : params.outsideLabelColor}
                            key={`${displayName}-${index}`}
                          >
                            {displayName}
                          </text>
                        ))}
                      </g>
                    </Popover>
                  </TooltipWrapper>
                </g>
              );
            });
          })}
        </>
      </ComponentWrapper>
    </svg>
  );
}

const styles = StyleSheet.create({
  pointer: {
    cursor: 'pointer',
  },
});
